/**
 * Logica del panel de ajustes de la IA, fuera del componente.
 *
 * Peticion de Ricardo (05/08/2026): que el jefe pueda cambiar como piensa y
 * como habla Androcles desde el propio kiosco. Aqui vive TODO lo que se puede
 * equivocar sin que se vea en pantalla —que se manda al backend, que se le
 * ensena al jefe cuando algo falla, como se guarda una instruccion— para poder
 * probarlo sin navegador. El componente solo pinta.
 *
 * Dos decisiones que conviene no deshacer:
 *
 *  - **Se manda solo lo que cambia.** `backend.agente.preferencias.aplicar_cambios`
 *    conserva lo que no venga. Mandar el objeto entero hace que el backend
 *    valide ajustes que el jefe no ha tocado, y un rechazo por algo que el no
 *    ha movido es un error imposible de entender.
 *  - **La lista de modelos que admiten temperatura NO se copia aqui.** La
 *    calcula el backend (`admite_temperatura`) y viaja en
 *    `opciones.temperatura_aplicable`. Duplicarla en el kiosco garantiza que
 *    algun dia digan cosas distintas.
 */
import { ErrorApi, type AjustesIA, type InstruccionIA } from '@/lib/api';

/**
 * Nombres en claro de los modelos. El jefe no tiene por que saber que es un
 * "claude-fable-5": lo que le importa es si piensa rapido o piensa despacio.
 * Lo que no este aqui se ensena tal cual llega: preferible un id feo a un
 * hueco en blanco cuando el backend anada un modelo nuevo.
 */
const MODELOS_EN_CLARO: Readonly<Record<string, string>> = {
  'claude-fable-5': 'Rápido de palabra (el de siempre)',
  'claude-sonnet-5': 'Equilibrado',
  'claude-opus-5': 'El que más piensa (va más despacio)',
  'claude-haiku-4-5-20251001': 'El más ligero',
  'claude-opus-4-8': 'El que más piensa, versión anterior',
};

/**
 * Prefijo con el que el backend marca los cerebros que corren en esta misma
 * maquina (Ollama). Ver `backend/agente/ollama.py`.
 */
const PREFIJO_LOCAL = 'ollama:';

/** Nombre presentable de un modelo. */
export function nombreDeModelo(id: string): string {
  if (id.startsWith(PREFIJO_LOCAL)) {
    // Los locales no tienen nombre comercial: se ensena el del modelo y, sobre
    // todo, que no sale de aqui. Es lo unico que le importa al jefe.
    return `${id.slice(PREFIJO_LOCAL.length)} — en este ordenador, sin coste`;
  }
  return MODELOS_EN_CLARO[id] ?? id;
}

/** true si ese cerebro corre en esta casa (mismo criterio que el backend). */
export function esModeloLocal(id: string): boolean {
  return id.startsWith(PREFIJO_LOCAL);
}

/**
 * A donde van los datos con ese cerebro puesto.
 *
 * Es el aviso que pidio Ricardo (09/09/2026): "me da mucho miedo la
 * informacion que pueda salir fuera". En cada turno se le manda al modelo el
 * contrato, los criterios, la memoria y las cifras de caja; con un cerebro de
 * Anthropic eso viaja a internet, con uno local no sale del ordenador. Que se
 * vea al elegir, no en un manual.
 */
export function avisoDePrivacidad(id: string): string {
  return esModeloLocal(id)
    ? 'No sale de este ordenador: la conversación y las cifras se quedan en casa.'
    : 'Se manda a Anthropic por internet: la pregunta, las cifras de caja y sus criterios.';
}

/**
 * Por que el control de riesgo puede aparecer apagado.
 *
 * Sin numeros ni jerga: el jefe no tiene que saber que `temperature` esta
 * deprecada en los modelos nuevos, solo que ese cerebro viene con el criterio
 * fijado y que hay otros donde si se puede mover.
 */
export const MOTIVO_TEMPERATURA_BLOQUEADA =
  'Este cerebro responde siempre con el mismo criterio y no deja cambiarlo. ' +
  'Si quiere poder moverlo, elija otro cerebro arriba y pulse Guardar.';

/** Como se lee un valor de riesgo, de 0 a 1, en palabras. */
export function etiquetaRiesgo(valor: number): string {
  if (valor <= 0.2) return 'Muy prudente: contesta siempre lo más seguro';
  if (valor <= 0.45) return 'Prudente: lo recomendable para hablar de dinero';
  if (valor <= 0.7) return 'Intermedio: se permite alguna vuelta';
  return 'Se arriesga: contesta con más libertad';
}

/**
 * Niveles de riesgo entre los que se elige en el panel.
 *
 * A proposito NO es una barra deslizante: arrastrar con precision es
 * justamente lo que peor se le da a unas manos de 88 anos, y un decimal de mas
 * no significa nada para quien lo usa. Cuatro opciones con nombre.
 */
export const NIVELES_RIESGO: readonly number[] = [0.1, 0.35, 0.6, 0.9];

/** El nivel de la lista mas parecido a un valor guardado a mano. */
export function nivelRiesgoMasCercano(valor: number): number {
  return NIVELES_RIESGO.reduce((mejor, nivel) =>
    Math.abs(nivel - valor) < Math.abs(mejor - valor) ? nivel : mejor,
  );
}

/** Claves de los ajustes, para recorrerlas sin olvidar ninguna. */
const CLAVES: readonly (keyof AjustesIA)[] = [
  'modelo_orquestador',
  'modelo_asesor',
  'modelo_contable',
  'modelo_nlu_voz',
  'modelo_consejero_senior',
  'ollama_url',
  'ollama_contexto',
  'ollama_timeout_s',
  'ollama_pensar',
  'temperatura',
  'max_iteraciones',
  'frases_respuesta',
  'contexto_adelantado',
  'memoria_activa',
  'motor_voz',
  'voz',
  'voz_en_streaming',
  'instrucciones',
];

/** true si dos bibliotecas de instrucciones dicen lo mismo. */
function mismasInstrucciones(a: InstruccionIA[], b: InstruccionIA[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((uno, i) => {
    const otro = b[i];
    return uno.nombre === otro.nombre && uno.texto === otro.texto && uno.activa === otro.activa;
  });
}

/**
 * Que hay que mandar al backend: solo las claves realmente tocadas.
 *
 * Args:
 *   guardados: lo ultimo que confirmo el servidor.
 *   borrador: lo que hay ahora mismo en pantalla.
 *
 * Returns:
 *   Objeto con las claves cambiadas (vacio si no hay nada que guardar).
 */
export function cambiosPendientes(
  guardados: AjustesIA,
  borrador: AjustesIA,
): Partial<AjustesIA> {
  const cambios: Partial<AjustesIA> = {};
  for (const clave of CLAVES) {
    if (clave === 'instrucciones') {
      if (!mismasInstrucciones(guardados.instrucciones, borrador.instrucciones)) {
        cambios.instrucciones = borrador.instrucciones;
      }
      continue;
    }
    if (guardados[clave] !== borrador[clave]) {
      // El tipo por clave ya lo garantiza `AjustesIA`; TypeScript no puede
      // seguir la union clave-a-clave en una asignacion dinamica.
      (cambios as Record<string, unknown>)[clave] = borrador[clave];
    }
  }
  return cambios;
}

/** true si hay algo pendiente de guardar (para encender el boton). */
export function hayCambios(guardados: AjustesIA, borrador: AjustesIA): boolean {
  return Object.keys(cambiosPendientes(guardados, borrador)).length > 0;
}

/**
 * true si, tras guardar estos cambios, hay que volver a pedir las opciones.
 *
 * `temperatura_aplicable` lo calcula el backend sobre el modelo GUARDADO. Al
 * cambiar de cerebro, la respuesta del PUT trae los ajustes pero no las
 * opciones, asi que el panel se quedaria diciendo que se puede mover el riesgo
 * cuando ya no, o al reves.
 */
export function tocaRecargarOpciones(cambios: Partial<AjustesIA>): boolean {
  // Cualquier cambio de cerebro: ademas del riesgo, el backend recalcula si
  // ese rol saca los datos de casa (`roles[].local`), que es el aviso que se
  // pinta al lado de cada uno.
  return Object.keys(cambios).some((clave) => clave.startsWith('modelo_'));
}

// ---------------------------------------------------------------------------
// Biblioteca de instrucciones
// ---------------------------------------------------------------------------

/**
 * Comprueba una instruccion antes de mandarla.
 *
 * Se valida aqui ademas de en el backend para que el jefe vea el motivo al
 * instante, sin esperar a un 400 en mitad de un guardado.
 *
 * Returns:
 *   El motivo en castellano, o `null` si la instruccion vale.
 */
export function validarInstruccion(nombre: string, texto: string): string | null {
  if (!nombre.trim()) return 'Póngale un nombre para poder encontrarla luego.';
  if (!texto.trim()) return 'Escriba qué quiere que haga; sin texto no dice nada.';
  return null;
}

/**
 * Guarda una instruccion en la biblioteca. Si ya hay una con ese nombre, la
 * sustituye: el jefe cree estar corrigiendo la suya, no creando una segunda.
 * Entra ACTIVA, que es lo que espera quien acaba de escribirla.
 */
export function guardarInstruccion(
  lista: InstruccionIA[],
  nombre: string,
  texto: string,
): InstruccionIA[] {
  const limpia: InstruccionIA = { nombre: nombre.trim(), texto: texto.trim(), activa: true };
  const existente = lista.findIndex((i) => i.nombre === limpia.nombre);
  if (existente < 0) return [...lista, limpia];
  const copia = [...lista];
  // Se respeta si el jefe la tenia apagada: guardar es corregir el texto,
  // no volver a encenderla a sus espaldas.
  copia[existente] = { ...limpia, activa: lista[existente].activa };
  return copia;
}

/** Enciende o apaga una instruccion sin tocar su texto. */
export function alternarInstruccion(lista: InstruccionIA[], nombre: string): InstruccionIA[] {
  return lista.map((i) => (i.nombre === nombre ? { ...i, activa: !i.activa } : i));
}

/** Quita una instruccion de la biblioteca. */
export function borrarInstruccion(lista: InstruccionIA[], nombre: string): InstruccionIA[] {
  return lista.filter((i) => i.nombre !== nombre);
}

// ---------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------

/**
 * Texto que se le ensena al jefe cuando algo falla.
 *
 * El motivo del servidor va TAL CUAL: `ErrorPreferencia` ya lo redacta en
 * castellano llano ("La temperatura tiene que estar entre 0.0 y 1.0."), y
 * reescribirlo aqui solo conseguiria que dijeran cosas distintas.
 */
export function mensajeDeFallo(fallo: unknown): string {
  if (fallo instanceof ErrorApi) return fallo.message;
  if (fallo instanceof Error && fallo.message) return fallo.message;
  return 'No he podido guardar los ajustes. Vuelva a intentarlo en un momento.';
}
